import { useEffect, useState } from 'react';

import usePxAuthContext from 'src/authentication/usePxAuthContext';
import GameSocketContextProvider from 'src/contexts/GameSocketContextProvider';
import Background from 'src/features/PixelStreaming/components/Background/Background';
import ContentViewer from 'src/features/PixelStreaming/components/ContentViewer/ContentViewer';
import HUD from 'src/features/PixelStreaming/components/HUD';
import PointsDisplay from 'src/features/PixelStreaming/components/PointsDisplay';
import PointsOfInterest from 'src/features/PixelStreaming/components/PointsOfInterest';
import ServerBrowserModalContainer from 'src/features/PixelStreaming/components/ServerBrowserModalContainer';
import SettingsOverlay from 'src/features/PixelStreaming/components/SettingsOverlay';
import { usePixelStreamingContext } from 'src/features/PixelStreaming/contexts/PixelStreamingContext';
import useDebugEmitUIInteract from 'src/features/PixelStreaming/hooks/useDebugEmitUIInteraction';
import useEmojisBinding from 'src/features/PixelStreaming/hooks/useEmojisBinding';
import useInitialPacket from 'src/features/PixelStreaming/hooks/useInitialPacket';
import useJoinGameSession from 'src/features/PixelStreaming/hooks/useJoinGameSession';
import useJoinSpace from 'src/features/PixelStreaming/hooks/useJoinSpace';
import useOpenExternalLink from 'src/features/PixelStreaming/hooks/useOpenExternalLink';
import useOpenModal from 'src/features/PixelStreaming/hooks/useOpenModal';
import useProfileUpdate from 'src/features/PixelStreaming/hooks/useProfileUpdate';
import useRefreshToken from 'src/features/PixelStreaming/hooks/useRefreshToken';
import useUserJoinAndExit from 'src/features/PixelStreaming/hooks/useUserJoinAndExit';
import InterlucentContextProvider from 'src/features/Interlucent/components/InterlucentContextProvider';
import EmojiGestureSelection from 'src/features/NewVisitor/layouts/EmojiGestureSelection';
import UsageLimitReachedModal from 'src/features/Usage/components/UsageLimitReachedModal';
import UsageLimitReachedScreen from 'src/features/Usage/components/UsageLimitReachedScreen';
import UsageContextProvider, { useUsageContext } from 'src/features/Usage/contexts/UsageContextProvider';
import { IMetaverse3DPage } from 'src/interfaces/IPage';
import { useGetRandomGameLiftSession } from 'src/queries/gamelift';

import ImageSlotViewer from '../../../PixelStreaming/components/ImageSlot/ImageSlotViewer';
import UploadContentModalContainer from '../../components/UploadContentModal/UploadContentModalContainer';

type Props = {
  page: IMetaverse3DPage;
};

function InterlucentMetaversePageBody({ page }: Props) {
  const { metaverse, space } = page;
  const metaverseId = metaverse._id;
  const { hasUsage } = useUsageContext();
  const { isStreaming } = usePixelStreamingContext();
  const { user, authToken } = usePxAuthContext();

  const { mapName, purewebConfiguration } = space.environment;
  const { aliasId } = purewebConfiguration;
  const [gameSessionId, setGameSessionId] = useState('');
  const { data: randomGameLiftSession } = useGetRandomGameLiftSession(
    metaverseId,
    aliasId,
    mapName
  );

  useEffect(() => {
    if (!randomGameLiftSession) return;
    setGameSessionId(randomGameLiftSession.GameSessionId);
  }, [randomGameLiftSession]);

  useJoinGameSession(gameSessionId);
  useInitialPacket(metaverseId, undefined, authToken, user);
  useRefreshToken();
  useOpenExternalLink();
  useOpenModal();
  useJoinSpace(space._id);
  useProfileUpdate();
  useDebugEmitUIInteract();
  useUserJoinAndExit();
  useEmojisBinding(Boolean(metaverseId && authToken && user));

  return (
    <>
      {!isStreaming && space.background && <Background source={space.background} />}
      <UsageLimitReachedScreen isOwner={false} logo={metaverse?.logo} />
      {hasUsage && (
        <>
          <HUD />
          <EmojiGestureSelection />
          <PointsDisplay />
          <PointsOfInterest />
          <ContentViewer metaverse={metaverse} />
          <ImageSlotViewer metaverse={metaverse} />
          <SettingsOverlay />
        </>
      )}
      <UploadContentModalContainer metaverseId={metaverseId} isSiteAdmin={false} />
      <UsageLimitReachedModal isOwner={false} />
      <ServerBrowserModalContainer
        aliasId={aliasId}
        query={{ metaverseId, mapName }}
        onGameSessionIdChange={setGameSessionId}
      />
    </>
  );
}

export default function InterlucentMetaversePage({ page }: Props) {
  const { authToken } = usePxAuthContext();
  const { modelId } = page.space.environment.purewebConfiguration;

  return (
    <UsageContextProvider metaverseId={page.metaverse._id}>
      <GameSocketContextProvider metaverseId={page.metaverse._id} authToken={authToken}>
        <InterlucentContextProvider appId={modelId}>
          <InterlucentMetaversePageBody page={page} />
        </InterlucentContextProvider>
      </GameSocketContextProvider>
    </UsageContextProvider>
  );
}
